import React, { useState, useEffect, useRef } from 'react';
import { Modal, Spin, Button, Tag, Space, Alert } from 'antd';
import {
    DownloadOutlined,
    ZoomInOutlined,
    ZoomOutOutlined,
    FullscreenOutlined,
    FilePdfOutlined,
    FileImageOutlined,
    FileUnknownOutlined,
} from '@ant-design/icons';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import axiosInstance from '../../../variables/api/axiosInstance';

/**
 * Modal de prévisualisation d'un document GED.
 *
 * Props :
 *   doc      - objet DocumentGedResponse (doit avoir ossanGedDocumentId, titre, mimeType)
 *   open     - booléen contrôlant l'affichage
 *   onClose  - callback de fermeture
 */
const DocumentPreviewModal = ({ doc, open, onClose }) => {
    const { t } = useTranslation('dossierSinistre');
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [zoom, setZoom] = useState(1);
    const prevBlobRef = useRef(null);

    const mimeType = doc?.mimeType || 'application/octet-stream';
    const isPdf = mimeType === 'application/pdf' || mimeType.includes('pdf');
    const isImage = mimeType.startsWith('image/');
    const canRender = isPdf || isImage;

    /* ── Chargement du blob à l'ouverture ── */
    useEffect(() => {
        if (!open || !doc?.ossanGedDocumentId) return;

        let cancelled = false;
        setLoading(true);
        setError(null);
        setZoom(1);

        axiosInstance
            .get(`/v1/ged/documents/${doc.ossanGedDocumentId}/telecharger`, {
                responseType: 'blob',
            })
            .then((res) => {
                if (cancelled) return;
                const blob = new Blob([res.data], { type: mimeType });
                const url = URL.createObjectURL(blob);
                prevBlobRef.current = url;
                setBlobUrl(url);
            })
            .catch(() => {
                if (!cancelled) setError(i18n.t('dossierSinistre:apercuDocument.chargementImpossible'));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, doc?.ossanGedDocumentId, mimeType]);

    /* ── Nettoyage de l'URL blob à la fermeture ── */
    useEffect(() => {
        if (!open && prevBlobRef.current) {
            URL.revokeObjectURL(prevBlobRef.current);
            prevBlobRef.current = null;
            setBlobUrl(null);
            setError(null);
        }
    }, [open]);

    /* ── Téléchargement direct ── */
    const telecharger = async () => {
        if (blobUrl) {
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = `${doc.titre || 'document'}.${isPdf ? 'pdf' : isImage ? mimeType.split('/')[1] : 'bin'}`;
            a.click();
            return;
        }
        try {
            const res = await axiosInstance.get(
                `/v1/ged/documents/${doc.ossanGedDocumentId}/telecharger`,
                { responseType: 'blob' }
            );
            const blob = new Blob([res.data], { type: mimeType });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${doc.titre || 'document'}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error(t('apercuDocument.telechargementImpossible'));
        }
    };

    /* ── Icône selon le type ── */
    const TypeIcon = isPdf ? FilePdfOutlined : isImage ? FileImageOutlined : FileUnknownOutlined;

    /* ── Rendu du contenu ── */
    const renderContent = () => {
        if (loading) {
            return (
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 500 }}>
                    <Spin size="large" tip={t('apercuDocument.chargement')} />
                </div>
            );
        }

        if (error) {
            return (
                <Alert
                    type="error"
                    showIcon
                    message={t('apercuDocument.erreurChargement')}
                    description={error}
                    style={{ borderRadius: 10 }}
                />
            );
        }

        if (!canRender && blobUrl) {
            return (
                <Alert
                    type="info"
                    showIcon
                    message={t('apercuDocument.previsualisationIndisponible')}
                    description={t('apercuDocument.typeNonPrevisualisable', { type: mimeType })}
                    style={{ borderRadius: 10 }}
                />
            );
        }

        if (!blobUrl) return null;

        if (isPdf) {
            return (
                <iframe
                    src={blobUrl}
                    title={doc.titre}
                    style={{
                        width: '100%',
                        height: 620,
                        border: 'none',
                        borderRadius: 8,
                        background: '#f4f4f4',
                    }}
                />
            );
        }

        if (isImage) {
            return (
                <div style={{ textAlign: 'center', overflow: 'auto', maxHeight: 620 }}>
                    <img
                        src={blobUrl}
                        alt={doc.titre}
                        style={{
                            maxWidth: '100%',
                            transform: `scale(${zoom})`,
                            transformOrigin: 'top center',
                            transition: 'transform 0.2s ease',
                            borderRadius: 8,
                        }}
                    />
                </div>
            );
        }

        return null;
    };

    return (
        <Modal
            open={open}
            onCancel={onClose}
            width={900}
            footer={null}
            destroyOnClose
            styles={{ body: { padding: '16px 24px 24px' } }}
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingRight: 40 }}>
                    <TypeIcon style={{ color: isPdf ? '#ef4444' : '#3b82f6', fontSize: 20 }} />
                    <span style={{ fontSize: 15, fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {doc?.titre || t('apercuDocument.document')}
                    </span>
                    {doc?.typeDocument && (
                        <Tag color="blue" style={{ borderRadius: 20, fontSize: 11 }}>
                            {doc.typeDocument.replace(/_/g, ' ')}
                        </Tag>
                    )}
                </div>
            }
        >
            {/* ── Barre d'outils ── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: '#6b7280' }}>
                    {mimeType}
                    {doc?.dateDocument ? ` · ${doc.dateDocument}` : ''}
                </div>

                <Space>
                    {isImage && (
                        <>
                            <Button
                                size="small"
                                icon={<ZoomOutOutlined />}
                                onClick={() => setZoom(z => Math.max(0.3, +(z - 0.2).toFixed(1)))}
                                disabled={zoom <= 0.3}
                            />
                            <span style={{ fontSize: 12, minWidth: 36, textAlign: 'center' }}>
                                {Math.round(zoom * 100)}%
                            </span>
                            <Button
                                size="small"
                                icon={<ZoomInOutlined />}
                                onClick={() => setZoom(z => Math.min(3, +(z + 0.2).toFixed(1)))}
                                disabled={zoom >= 3}
                            />
                        </>
                    )}
                    {isPdf && blobUrl && (
                        <Button
                            size="small"
                            icon={<FullscreenOutlined />}
                            onClick={() => window.open(blobUrl, '_blank')}
                        >
                            {t('apercuDocument.pleinEcran')}
                        </Button>
                    )}
                    <Button
                        size="small"
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={telecharger}
                        style={{ background: '#1a6b3c', borderColor: '#1a6b3c' }}
                    >
                        {t('apercuDocument.telecharger')}
                    </Button>
                </Space>
            </div>

            {/* ── Contenu principal ── */}
            {renderContent()}
        </Modal>
    );
};

export default DocumentPreviewModal;